import React, { useState, useRef, useEffect } from 'react';
import { useStoreLocation } from '../contexts/LocationContext';
import { 
  MapPin, 
  ChevronDown, 
  Check, 
  Building2, 
  Lock, 
  User, 
  Layers, 
  Shield, 
  Info, 
  SlidersHorizontal, 
  Compass 
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { LocationHierarchyRole } from '../types';

export const LocationSwitcher: React.FC = () => {
  const {
    hierarchyRole,
    setHierarchyRole,
    activeLocationId,
    activeLocation,
    availableLocations,
    changeLocation,
    organizationName,
    assignedRegionName,
    isConsolidated
  } = useStoreLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [showRoleSelector, setShowRoleSelector] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowRoleSelector(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const rolesList: { id: LocationHierarchyRole; label: string; badge: string; desc: string }[] = [
    {
      id: 'executive',
      label: 'Business Owner / Executive',
      badge: 'Global Access',
      desc: 'Company-wide financial totals, drill into any store, full payroll exports.'
    },
    {
      id: 'regional_manager',
      label: 'Regional / Area Manager',
      badge: 'Multi-Store Scoped',
      desc: 'Scoped only to assigned stores in North Region (Downtown & Uptown).'
    },
    {
      id: 'manager',
      label: 'Store General Manager',
      badge: 'Single-Store Locked',
      desc: 'Locked to single store: upload POS, jar tips, resolve disputes.'
    },
    {
      id: 'employee',
      label: 'Employee / Server',
      badge: 'Personal Staff',
      desc: 'Personal shift earnings calendar and tip sign-off.'
    }
  ];

  return (
    <div className="w-full bg-white border-b border-gray-200/80 px-4 md:px-8 py-3 shadow-xs">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-3">
        
        {/* Left Side: Active Location Badge & Dropdown */}
        <div className="flex flex-wrap items-center gap-3 relative" ref={dropdownRef}>
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <Building2 size={15} className="text-blue-600 shrink-0" />
            <span className="hidden sm:inline">{organizationName}:</span>
          </div>

          {/* ============================================================ */}
          {/* TIER 1 & 2: Interactive Location Dropdown (Exec / Regional) */}
          {/* ============================================================ */}
          {(hierarchyRole === 'executive' || hierarchyRole === 'regional_manager') && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-xl bg-slate-900 text-white hover:bg-slate-800 transition-all font-semibold text-sm shadow-xs active:scale-[0.98] cursor-pointer"
                aria-expanded={isOpen}
              >
                <MapPin size={16} className="text-blue-400 shrink-0" />
                <span className="tracking-tight font-medium">
                  {hierarchyRole === 'regional_manager' && activeLocationId === 'all'
                    ? assignedRegionName
                    : (activeLocation?.name || 'Store Location')}
                </span>
                <ChevronDown
                  size={15}
                  className={`text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180 text-white' : ''}`}
                />
              </button>

              {/* Dropdown Menu */}
              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 6, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 4, scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                    className="absolute left-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-200 py-2 z-50 text-gray-800 overflow-hidden"
                  >
                    <div className="px-3.5 py-2 border-b border-gray-100 bg-slate-50/70">
                      <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                        {hierarchyRole === 'executive'
                          ? 'Select Location (Global Access)'
                          : `Assigned Stores (${assignedRegionName})`}
                      </p>
                    </div>

                    <div className="max-h-72 overflow-y-auto py-1">
                      {availableLocations.map((loc) => {
                        const isSelected = activeLocationId === loc.id;
                        return (
                          <button
                            key={loc.id}
                            type="button"
                            onClick={() => {
                              changeLocation(loc.id);
                              setIsOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors cursor-pointer ${
                              isSelected
                                ? 'bg-blue-50/80 text-blue-900 font-semibold'
                                : 'hover:bg-gray-50 text-gray-700'
                            }`}
                          >
                            <div className="flex items-center gap-2.5 min-w-0 pr-2">
                              {loc.isConsolidatedOption ? (
                                <Layers size={16} className="text-blue-600 shrink-0" />
                              ) : (
                                <MapPin size={16} className={isSelected ? 'text-blue-600 shrink-0' : 'text-gray-400 shrink-0'} />
                              )}
                              <div className="truncate">
                                <span className="block truncate">{loc.name}</span>
                                {loc.region && !loc.isConsolidatedOption && (
                                  <span className="text-[11px] text-gray-400 font-normal block">
                                    {loc.city ? `${loc.city}, ${loc.state} • ` : ''}{loc.region}
                                  </span>
                                )}
                              </div>
                            </div>
                            {isSelected && (
                              <Check size={16} className="text-blue-600 shrink-0" />
                            )}
                          </button>
                        );
                      })}
                    </div>

                    <div className="px-3.5 py-2 border-t border-gray-100 bg-slate-50 text-[11px] text-gray-500 leading-snug">
                      {hierarchyRole === 'executive' ? (
                        <span>Showing consolidated corporate data and all store units.</span>
                      ) : (
                        <span>Only your assigned North Region stores are accessible.</span>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* ============================================================ */}
          {/* TIER 3: Store General Manager (Locked Single-Store Badge)    */}
          {/* ============================================================ */}
          {hierarchyRole === 'manager' && (
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-slate-100 border border-slate-300/80 text-slate-800 text-sm font-semibold shadow-xs">
              <MapPin size={15} className="text-emerald-600 shrink-0" />
              <span>{activeLocation?.name || 'Assigned Store'}</span>
              <span className="flex items-center gap-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider bg-white px-2 py-0.5 rounded-md border border-slate-200">
                <Lock size={10} className="text-slate-400" />
                Locked Store
              </span>
            </div>
          )}

          {/* ============================================================ */}
          {/* TIER 4: Employee / Server (Personal Staff Access Badge)      */}
          {/* ============================================================ */}
          {hierarchyRole === 'employee' && (
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-indigo-50 border border-indigo-200 text-indigo-950 text-sm font-semibold shadow-xs">
              <User size={15} className="text-indigo-600 shrink-0" />
              <span>{activeLocation?.name || 'Personal Staff Access'}</span>
            </div>
          )}

          {/* Consolidated indicator pill for executive/regional */}
          {isConsolidated && (hierarchyRole === 'executive' || hierarchyRole === 'regional_manager') && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
              <Layers size={13} />
              Consolidated Rollup Active
            </span>
          )}
        </div>

        {/* Right Side: Persona / Role Preview Bar */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowRoleSelector(!showRoleSelector)}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-xs font-semibold transition-colors cursor-pointer"
            >
              <SlidersHorizontal size={14} className="text-blue-600 shrink-0" />
              <span className="hidden sm:inline text-gray-500 font-normal">Hierarchy Mode:</span>
              <span className="font-bold text-gray-900">
                {rolesList.find(r => r.id === hierarchyRole)?.label.split(' / ')[0]}
              </span>
              <ChevronDown size={13} className="text-gray-400" />
            </button>

            {/* Role Switcher Popover */}
            <AnimatePresence>
              {showRoleSelector && (
                <motion.div
                  initial={{ opacity: 0, y: 6, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 4, scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-2 w-84 bg-white rounded-2xl shadow-xl border border-gray-200 p-2 z-50 text-gray-800"
                >
                  <div className="px-3 py-2 border-b border-gray-100">
                    <p className="text-xs font-bold text-gray-900 flex items-center gap-1.5">
                      <Compass size={14} className="text-blue-600" />
                      Test Multi-Location Hierarchy Roles
                    </p>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      Switch personas to experience each level of permissions and view bounds:
                    </p>
                  </div>

                  <div className="space-y-1 py-1.5">
                    {rolesList.map(r => {
                      const isSelected = hierarchyRole === r.id;
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => {
                            setHierarchyRole(r.id);
                            setShowRoleSelector(false);
                          }}
                          className={`w-full text-left p-2.5 rounded-xl text-xs transition-all cursor-pointer ${
                            isSelected
                              ? 'bg-blue-50 border border-blue-200 text-blue-950 font-medium'
                              : 'hover:bg-gray-50 text-gray-700 border border-transparent'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-bold text-xs">{r.label}</span>
                            <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                              isSelected
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {r.badge}
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-500 leading-normal">{r.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

      </div>
    </div>
  );
};
